import React from 'react';
import { Card, Typography, Spin, Alert, Input, Popconfirm, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useGetProjSystems } from '../../../hooks/genericHooks';
import { useNavigate } from 'react-router-dom';
import { ProjSystems } from '../../../types';
import StandardTable from '../../../components/common/StandardTable';
import Button from '../../../components/common/Button';
const { Title } = Typography;

const ToPlnProjSystemsList: React.FC = () => {
  const { data, isLoading, error, refetch } = useGetProjSystems();
  const [search, setSearch] = React.useState('');
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const navigate = useNavigate();

  // Filtered data for search
  const filteredData = React.useMemo(() => {
    if (!data?.data) return [];
    return data.data.filter(
      (item) =>
        (typeof item.sys_id === 'string' && item.sys_id.toLowerCase().includes(search.toLowerCase())) ||
        (typeof item.sys_name === 'string' && item.sys_name.toLowerCase().includes(search.toLowerCase()))
    );
  }, [data, search]);

  const handleDelete = async (record: any) => {
    setDeleteLoading(true);
    try {
      // await deleteData('to_pln_proj_systems', 'sys_id', record.sys_id);
      message.success('System deleted (mock)!');
      refetch && refetch();
    } catch (err: any) {
      message.error('Failed to delete: ' + (err?.message || 'Unknown error'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns: any[] = [
    {
      title: 'System ID',
      dataIndex: 'sys_id',
      key: 'sys_id',
      sorter: (a: any, b: any) => a.sys_id.localeCompare(b.sys_id),
      width: 180,
      exportValue: (r: ProjSystems) => r.sys_id,
    },
    {
      title: 'System Name',
      dataIndex: 'sys_name',
      key: 'sys_name',
      sorter: (a: any, b: any) => a.sys_name.localeCompare(b.sys_name),
      exportValue: (r: ProjSystems) => r.sys_name,
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center' as const,
      render: (_: any, record: any) => (
        <span>
          <Button
            action="edit"
            icon={<EditOutlined />}
            size="small"
            type="primary"
            style={{ marginRight: 8 }}
            onClick={() => navigate(`/to/planning/proj_systems/edit/${record.sys_id}`)}
          />
          <Popconfirm
            title="Are you sure to delete this system?"
            onConfirm={() => handleDelete(record)}
            okButtonProps={{ loading: deleteLoading }}
          >
            <Button action="delete" icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </span>
      ),
      width: 160,
    },
  ];

  const handleAddSystem = () => {
    // TODO: Implement navigation or modal for adding a new system
  };

  return (
    <Card style={{ margin: 16, padding: 0 }} styles={{ body: { padding: 0 } }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px 8px 24px', borderBottom: '1px solid #f0f0f0' }}>
        <Title level={4} style={{ margin: 0 }}>Project Systems</Title>
        <Button action="add" type="primary" icon={<PlusOutlined />} onClick={handleAddSystem}>
          Add System
        </Button>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', padding: '8px 24px 8px 24px', borderBottom: '1px solid #f0f0f0' }}>
        <Input
          type="text"
          placeholder="Search by ID or Name"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ minWidth: 220, maxWidth: 320 }}
          allowClear
        />
      </div>
      <div style={{ padding: '8px 24px 16px 24px' }}>
        {error && <Alert type="error" message="Failed to load project systems" description={error.message} showIcon style={{ marginBottom: 8 }} />}
        {isLoading ? (
          <Spin />
        ) : (
          <StandardTable
            columns={columns}
            dataSource={filteredData}
            rowKey="sys_id"
            size="small"
            loading={deleteLoading}
            enableExport={true}
          />
        )}
      </div>
    </Card>
  );
};

export default ToPlnProjSystemsList;
